import { useState, type FC } from "react";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import Divider from "../ui/Divider";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Alert } from "../ui/alert";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ksUserQK } from "../../lib/query-keys";
import { useUpdateSiret } from "../../lib/hooks/useUpdateSiret";
import { normaliserSiret, SIRET_MESSAGE_ERREUR, siretValide } from "../../lib/helpers/siret_helpers";

interface Props {
  userId: string;
  open: boolean;
  setOpen: (val: boolean) => void;
  onSubmit: (siret?: string) => void;
}

const DemanderSiretDialog: FC<Props> = ({ userId, open, setOpen, onSubmit }) => {
  const [siret, setSiret] = useState("");
  const queryClient = useQueryClient();
  const { mutate: updateSiret, isPending } = useUpdateSiret();

  const siretNormalise = normaliserSiret(siret);
  const estValide = siretValide(siret);
  const erreurSiret = siret.trim() !== "" && !estValide;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!estValide) return;
    updateSiret(
      { userId, siret: siretNormalise },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: [ksUserQK] });
          onSubmit(siretNormalise);
          setOpen(false);
        },
        onError: (e) => {
          console.error(e);
          toast.error("Une erreur s'est produite, veuillez réessayer ultérieurement.");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='max-w-[650px]'>
        <DialogHeader>
          <DialogTitle>SIRET</DialogTitle>
        </DialogHeader>
        {/* Body */}
        <Divider />
        <form onSubmit={handleSubmit}>
          <Alert className='text-sm bg-orange-50 border-orange-200'>
            <div className='text-orange-700 font-semibold mb-1'>Important</div>
            Un numéro SIRET valide (14 chiffres) est requis pour l'attestation d'éco-contribution.
          </Alert>
          <div className='my-5'>
            <Input
              type='text'
              placeholder='Votre numéro SIRET (14 chiffres)'
              value={siret}
              onChange={(e) => setSiret(e.currentTarget.value)}
              inputMode='numeric'
              aria-invalid={erreurSiret}
              required
            />
            {erreurSiret && (
              <div className='mt-2 text-sm text-red-600'>{SIRET_MESSAGE_ERREUR}</div>
            )}
          </div>
          {/* Footer */}
          <DialogFooter>
            <div>
              <div className='flex flex-row gap-5 justify-end'>
                <DialogClose asChild>
                  <Button variant={"outline"}>Annuler</Button>
                </DialogClose>
                <Button loading={isPending} type='submit' disabled={!estValide}>
                  Valider
                </Button>
              </div>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DemanderSiretDialog;
